import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Users, FileText, Store, AlertTriangle, Copy, ChevronRight, CheckCircle } from 'lucide-react';
import { fetchDraftPreview } from './api';
import { fetchPackMeta } from './api';
import type { PreviewResponse, PreviewFilters } from './types';
import { buildOperatorGroups, type OperatorGroup } from './grouping';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { InlineFormatEditor } from '@/components/pack-locking/InlineFormatEditor';

// Utility function to shorten IDs for display
const shortId = (id: string | number): string => {
  const idStr = id.toString();
  // If it's a UUID (36 chars), show first 6
  if (idStr.length === 36 && idStr.includes('-')) {
    return idStr.substring(0, 6);
  }
  // If it's numeric, show as is
  return idStr;
};

// Utility function to copy ID to clipboard
const copyIdToClipboard = (id: string | number, type: string) => {
  navigator.clipboard.writeText(id.toString());
  // You can add a toast notification here if you have a toast system
  console.log(`Copied ${type} ${id}`);
};

interface PackPreviewViewProps {
  packId: string;
  showIds?: boolean;
  onOperatorAssignmentChange?: () => void;
}

export const PackPreviewView: React.FC<PackPreviewViewProps> = ({ packId, showIds = false, onOperatorAssignmentChange }) => {
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [packMeta, setPackMeta] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'signoff' | 'table'>('signoff');
  
  const [filters, setFilters] = useState<PreviewFilters>({
    page: 1,
    pageSize: 25,
    operatorId: undefined,
    formatId: undefined,
    region: undefined,
    unresolvedOnly: false
  });

  const handleFilterChange = (key: keyof PreviewFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setFilters(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  // Export current preview data as CSV with IDs
  const exportIdsToCSV = () => {
    if (!preview) return;
    
    // Build CSV data from items[].assignments
    const csvData = preview.items.flatMap(item => 
      item.assignments.flatMap(assignment => 
        assignment.formats.map(format => [
          item.store_id,
          item.store_name,
          format.id,
          format.name,
          assignment.operator?.id || '',
          assignment.operator?.name || '',
          format.count
        ])
      )
    );
    
    // Add header row
    const csvContent = [
      ['store_id', 'store_name', 'format_id', 'format_name', 'operator_id', 'operator_name', 'count'],
      ...csvData
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    
    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pack-${packId}-ids-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Check if pack can be locked
  const canLock = preview && 
    preview.summary.unresolvedCount === 0 && 
    preview.summary.storeCount > 0 && 
    preview.summary.unitCount > 0;

  // Build operator groups for sign-off view
  const operatorGroups = preview ? buildOperatorGroups(preview) : [];

  // Get unique operators, formats, and regions for filters
  const uniqueOperators = preview ? [...new Set(preview.summary.operators.map(op => ({ id: op.id, name: op.name })))] : [];
  const uniqueFormats = preview ? [...new Set(preview.items.flatMap(item => 
    item.assignments.flatMap(assignment => 
      assignment.formats.map(f => ({ id: f.id, name: f.name }))
    )
  ))] : [];
  const uniqueRegions = preview ? [...new Set(preview.items.map(item => item.region).filter((region): region is string => !!region))] : [];

  // Get selected operator group
  const selectedOperator = selectedOperatorId 
    ? operatorGroups.find(g => g.operatorId === selectedOperatorId)
    : operatorGroups[0]; // Default to first operator

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load pack metadata first
      const metaData = await fetchPackMeta(packId);
      setPackMeta(metaData);
      
      // Then load preview data
      const previewData = await fetchDraftPreview(packId, filters);
      setPreview(previewData);
      setError(null);
      
      // Auto-select first operator if none selected
      if (!selectedOperatorId && previewData.items.length > 0) {
        const firstOp = previewData.items.find(item => item.operator)?.operator?.id;
        setSelectedOperatorId(firstOp || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [packId, filters]);

  // Handle operator assignment updates
  const handleOperatorAssignment = async (storeId: string, formatId: string, operatorId: string) => {
    try {
      const response = await fetch(`/api/packs/${packId}/assign-operator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          storeId,
          mediaFormatId: formatId,
          operatorId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update operator assignment');
      }

      // Refresh the preview data to show the updated assignment
      await loadData();
      
      // Notify parent components that operator assignments have changed
      onOperatorAssignmentChange?.();
    } catch (error) {
      console.error('Error updating operator assignment:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-4" />
              <p className="text-lg font-semibold">Error loading preview</p>
              <p className="text-sm mt-2">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview || preview.summary.storeCount === 0) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              <Store className="h-8 w-8 mx-auto mb-4" />
              <p className="text-lg font-semibold">No stores in draft</p>
              <p className="text-sm mt-2">This pack draft contains no stores.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show unresolved issues banner if any
  const hasUnresolvedIssues = preview.summary.unresolvedCount > 0;

  return (
    <div className="space-y-6">
      {/* Pack Header */}
      {packMeta && (
        <Card>
          <CardHeader>
            {packMeta.updatedAt && (
              <div className="text-sm text-muted-foreground mb-2">
                <strong>Last Updated:</strong> {new Date(packMeta.updatedAt).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">
                  {packMeta.name}
                  {showIds && (
                    <Badge 
                      variant="outline" 
                      className="ml-2 text-xs font-mono cursor-pointer hover:bg-muted"
                      onClick={() => copyIdToClipboard(packMeta.id, 'pack_id')}
                      title={`Click to copy full pack ID: ${packMeta.id}`}
                    >
                      pack_id: {shortId(packMeta.id)}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-2">
                  <Badge variant="outline">
                    {packMeta.retailer.name}
                    {showIds && (
                      <span 
                        className="ml-1 text-xs font-mono cursor-pointer hover:bg-muted px-1 rounded"
                        onClick={() => copyIdToClipboard(packMeta.retailer.id, 'retailer_id')}
                        title={`Click to copy retailer ID: ${packMeta.retailer.id}`}
                      >
                        · {shortId(packMeta.retailer.id)}
                      </span>
                    )}
                  </Badge>
                  <Badge variant="secondary">Pack ID: {packMeta.id}</Badge>
                </CardDescription>
              </div>
              <div className="text-right">
                <Badge variant={packMeta.status === 'locked' ? 'destructive' : 'default'}>
                  {packMeta.status === 'locked' ? 'Locked' : 'Unlocked'}
                </Badge>
                <div className="mt-2">
                  <Badge variant="outline" className="text-xs">
                    Source: Draft (live)
                  </Badge>
                </div>
                {showIds && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={exportIdsToCSV}
                  >
                    Export IDs (CSV)
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Pack Status Summary */}
      <Card className={preview.summary.unresolvedCount > 0 ? "border-destructive/50 bg-destructive/5" : "border-green-500/50 bg-green-50"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {preview.summary.unresolvedCount > 0 ? (
              <>
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Pack Cannot Be Locked
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                Pack Ready to Lock
              </>
            )}
          </CardTitle>
          <CardDescription>
            {preview.summary.unresolvedCount > 0 
              ? `${preview.summary.unresolvedCount} issue${preview.summary.unresolvedCount !== 1 ? 's' : ''} must be resolved before locking`
              : "All formats have operators assigned and the pack is ready to lock"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{preview.summary.storeCount}</div>
              <div className="text-xs text-muted-foreground">Stores</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{preview.summary.unitCount}</div>
              <div className="text-xs text-muted-foreground">Total Units</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{preview.summary.operators.length}</div>
              <div className="text-xs text-muted-foreground">Operators</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${preview.summary.unresolvedCount > 0 ? 'text-destructive' : 'text-green-600'}`}>
                {preview.summary.unresolvedCount}
              </div>
              <div className="text-xs text-muted-foreground">Unresolved</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lock Status Banner */}
      {preview && !canLock && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Pack cannot be locked</AlertTitle>
          <AlertDescription>
            {preview.summary.unresolvedCount > 0 && (
              <div className="mt-2">
                <strong>Issues to resolve:</strong>
                <ul className="list-disc list-inside mt-1">
                  {preview.diagnostics.missingOperators.length > 0 && (
                    <li>Missing operators for some formats</li>
                  )}
                  {preview.diagnostics.noEligibleGroup.length > 0 && (
                    <li>No eligible formats for some stores</li>
                  )}
                  {preview.diagnostics.policyConflicts.length > 0 && (
                    <li>Policy conflicts detected</li>
                  )}
                  {preview.diagnostics.formatNameDrift.length > 0 && (
                    <li>Format name mismatches detected ({preview.diagnostics.formatNameDrift.length} stores affected)</li>
                  )}
                </ul>
              </div>
            )}
            {preview.summary.storeCount === 0 && (
              <div className="mt-2">No stores in draft</div>
            )}
            {preview.summary.unitCount === 0 && (
              <div className="mt-2">No units in draft</div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Unresolved Issues Banner */}
      {hasUnresolvedIssues && (
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <p className="font-semibold">Resolve {preview.summary.unresolvedCount} issues before locking</p>
                <p className="text-sm">Fix the problems below to enable pack locking.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Name Drift Details */}
      {preview.diagnostics.formatNameDrift.length > 0 && (
        <Card className="border-amber-500 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Format Name Mismatches Detected
            </CardTitle>
            <CardDescription className="text-amber-700">
              The following format names in the database don't match what's expected in the UI.
              This needs to be resolved before the pack can be locked.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {preview.diagnostics.formatNameDrift
                .filter((drift, index, arr) => 
                  // Show unique format mismatches (not per-store)
                  arr.findIndex(d => d.formatId === drift.formatId) === index
                )
                .map((drift) => (
                  <div key={drift.formatId} className="p-3 bg-amber-100 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                            <p className="font-medium text-amber-900">
                                Format ID {drift.formatId}: <span className="text-red-600">{drift.oldCanonicalName}</span> → <span className="text-green-600">{drift.newCanonicalName}</span>
                              </p>
                        <p className="text-sm text-amber-700">
                          Affects {preview.diagnostics.formatNameDrift.filter(d => d.formatId === drift.formatId).length} stores
                        </p>
                      </div>
                      <Badge variant="outline" className="border-amber-300 text-amber-800">
                        Data Fix Required
                      </Badge>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Toggle */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'signoff' | 'table')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signoff">Sign-off Preview</TabsTrigger>
              <TabsTrigger value="table">Table View</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter stores and formats</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Operator Filter */}
            <div>
              <Label htmlFor="operator-filter">Operator</Label>
              <Select
                value={filters.operatorId || 'all'}
                onValueChange={(value) => handleFilterChange('operatorId', value === 'all' ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Operators" />
                </SelectTrigger>
                                  <SelectContent>
                    <SelectItem value="all">All Operators</SelectItem>
                    {uniqueOperators.map((op) => (
                      <SelectItem key={op.id} value={op.id}>
                        {op.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
              </Select>
            </div>

            {/* Format Filter */}
            <div>
              <Label htmlFor="format-filter">Format</Label>
              <Select
                value={filters.formatId || 'all'}
                onValueChange={(value) => handleFilterChange('formatId', value === 'all' ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Formats" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Formats</SelectItem>
                  {uniqueFormats.map((format) => (
                    <SelectItem key={format.id} value={format.id}>
                      {format.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Region Filter */}
            <div>
              <Label htmlFor="region-filter">Region</Label>
              <Select
                value={filters.region || 'all'}
                onValueChange={(value) => handleFilterChange('region', value === 'all' ? undefined : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {uniqueRegions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Unresolved Only Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="unresolved-only"
                checked={filters.unresolvedOnly || false}
                onCheckedChange={(checked) => handleFilterChange('unresolvedOnly', checked)}
              />
              <Label htmlFor="unresolved-only">Unresolved only</Label>
            </div>

            {/* Page Size */}
            <div>
              <Label htmlFor="page-size">Page Size</Label>
              <Select
                value={filters.pageSize?.toString() || '25'}
                onValueChange={(value) => handlePageSizeChange(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sign-off Preview View */}
      {viewMode === 'signoff' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Operator List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Operators
                </CardTitle>
                <CardDescription>Select an operator to view stores</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {operatorGroups.map((group, index) => (
                    <button
                      key={`${group.operatorId || 'unassigned'}-${index}`}
                      onClick={() => setSelectedOperatorId(group.operatorId)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedOperator?.operatorId === group.operatorId
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">
                            {group.operatorName}
                            {showIds && group.operatorId && (
                              <Badge 
                                variant="outline" 
                                className="ml-2 text-xs font-mono cursor-pointer hover:bg-muted"
                                onClick={() => copyIdToClipboard(group.operatorId!, 'operator_id')}
                                title={`Click to copy operator ID: ${group.operatorId}`}
                              >
                                {shortId(group.operatorId)}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {group.storeCount} stores • {group.unitCount} units
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Panel - Selected Operator's Stores */}
          <div className="lg:col-span-3">
            {selectedOperator ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {selectedOperator.operatorName}
                        {selectedOperator.operatorId && (
                          <Badge variant="outline" className="text-xs">
                            ID: {selectedOperator.operatorId}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        {selectedOperator.storeCount} stores • {selectedOperator.unitCount} units
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const summary = `${selectedOperator.operatorName}: ${selectedOperator.storeCount} stores, ${selectedOperator.unitCount} units`;
                        navigator.clipboard.writeText(summary);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy summary
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Unassigned Warning */}
                  {selectedOperator.operatorId === null && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          These stores lack an operator assignment.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Stores Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Store</TableHead>
                        <TableHead>Formats</TableHead>
                        <TableHead>Warnings</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOperator.stores.map((store) => (
                        <TableRow key={store.store_id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {store.store_name}
                                {showIds && (
                                  <Badge 
                                    variant="outline" 
                                    className="ml-2 text-xs font-mono cursor-pointer hover:bg-muted"
                                    onClick={() => copyIdToClipboard(store.store_id, 'store_id')}
                                    title={`Click to copy store ID: ${store.store_id}`}
                                  >
                                    {shortId(store.store_id)}
                                  </Badge>
                                )}
                              </div>
                              {store.region && (
                                <Badge variant="secondary" className="text-xs mt-1">
                                  {store.region}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {store.formats.map((format, index) => {
                                // If this is an unassigned operator group, use the inline editor
                                if (selectedOperator.operatorId === null) {
                                  return (
                                    <InlineFormatEditor
                                      key={`${format.id}-${index}`}
                                      format={format}
                                      storeId={store.store_id}
                                      storeName={store.store_name}
                                      operators={preview.summary.operators}
                                      onSave={handleOperatorAssignment}
                                      showIds={showIds}
                                    />
                                  );
                                }
                                
                                // Otherwise show as regular badge
                                return (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {format.name} ×{format.count}
                                    {showIds && (
                                      <span className="ml-1 font-mono">
                                        · {shortId(format.id)}
                                      </span>
                                    )}
                                    {format.canonical_name !== format.name && (
                                      <span 
                                        className="ml-1 text-xs text-muted-foreground cursor-help"
                                        title={`Canonical name: ${format.canonical_name}`}
                                      >
                                        (≠ {format.canonical_name})
                                      </span>
                                    )}
                                  </Badge>
                                );
                              })}
                            </div>
                          </TableCell>
                          <TableCell>
                            {store.warnings.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {store.warnings.map((warning, index) => (
                                  <Badge key={index} variant="destructive" className="text-xs">
                                    {warning}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">None</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-4" />
                    <p>Select an operator to view their stores</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <>
          {/* Summary Panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Format */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  By Format
                </CardTitle>
                <CardDescription>Unit counts by format (current page)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                                     {preview.summary.operators.slice(0, 5).map((op, index) => (
                     <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                       <div className="font-medium">{op.name}</div>
                       <Badge variant="outline">{op.storeCount} stores</Badge>
                     </div>
                   ))}
                </div>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="h-5 w-5" />
                  Summary
                </CardTitle>
                <CardDescription>Current page overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="font-medium">Stores</div>
                    <Badge variant="secondary">{preview.items.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="font-medium">Total Units</div>
                    <Badge variant="outline">
                      {preview.items.reduce((sum, item) => 
                        sum + item.formats.reduce((itemSum, f) => itemSum + (f.count || 0), 0), 0
                      )}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="font-medium">Unresolved</div>
                    <Badge variant={preview.summary.unresolvedCount > 0 ? "destructive" : "secondary"}>
                      {preview.summary.unresolvedCount}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stores Table */}
          <Card>
            <CardHeader>
              <CardTitle>Stores</CardTitle>
              <CardDescription>
                Showing {preview.items.length} stores on this page
                {preview.paging.total > preview.paging.pageSize && 
                  ` of ${preview.paging.total} total`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Store</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>Selected Group</TableHead>
                    <TableHead>Formats</TableHead>
                    <TableHead>Warnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.map((item) => (
                    <TableRow key={item.store_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.store_name}</div>
                          {item.region && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              {item.region}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.operator ? (
                          <div>
                            <div className="font-medium">{item.operator.name}</div>
                            <div className="text-xs text-muted-foreground">ID: {item.operator.id}</div>
                          </div>
                        ) : (
                          <Badge variant="outline">Unassigned</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.selected_group_id ? (
                          <Badge variant="secondary">{item.selected_group_id}</Badge>
                        ) : (
                          <span className="text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {item.formats.map((format, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {format.name} ×{format.count}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.warnings && item.warnings.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.warnings.map((warning, index) => (
                              <Badge key={index} variant="destructive" className="text-xs">
                                {warning === 'MISSING_OPERATOR' ? 'Missing Operator' : 
                                 warning === 'NO_ELIGIBLE_FORMATS' ? 'No Formats' : warning}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs text-green-600">
                            Ready
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Pagination */}
      {preview.paging.total > preview.paging.pageSize && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {preview.paging.page} of {Math.ceil(preview.paging.total / preview.paging.pageSize)}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(preview.paging.page - 1)}
                  disabled={preview.paging.page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {preview.paging.page} of {Math.ceil(preview.paging.total / preview.paging.pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(preview.paging.page + 1)}
                  disabled={preview.paging.page >= Math.ceil(preview.paging.total / preview.paging.pageSize)}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
        {/* Unassigned Formats Section - Show all formats that need operators */}
        {preview.summary.unresolvedCount > 0 && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Unassigned Formats ({preview.summary.unresolvedCount})
              </CardTitle>
              <CardDescription>
                The following formats need operators assigned before this pack can be locked
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {preview.items
                  .filter(item => item.assignments.some(assignment => assignment.operator === null))
                  .map((item) => (
                    <div key={item.store_id} className="border rounded-lg p-4 bg-background">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">
                          {item.store_name}
                          {showIds && (
                            <Badge variant="outline" className="ml-2 text-xs font-mono">
                              Store ID: {item.store_id}
                            </Badge>
                          )}
                        </h4>
                        <Badge variant="destructive" className="text-xs">
                          {item.assignments.filter(a => a.operator === null).length} unassigned
                        </Badge>
                      </div>
                      
                      {/* Show unassigned formats with inline editors */}
                      {item.assignments
                        .filter(assignment => assignment.operator === null)
                        .map((assignment, assignmentIndex) => (
                          <div key={assignmentIndex} className="border-t pt-3">
                            <div className="text-sm text-muted-foreground mb-2">
                              Formats needing operator assignment:
                            </div>
                            <div className="space-y-2">
                              {assignment.formats.map((format, formatIndex) => (
                                <InlineFormatEditor
                                  key={`${format.id}-${formatIndex}`}
                                  format={format}
                                  storeId={item.store_id}
                                  storeName={item.store_name}
                                  operators={preview.summary.operators}
                                  onSave={handleOperatorAssignment}
                                  showIds={showIds}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
};
